import { useTranslation } from 'react-i18next'
import { addDays, dayLabelShort, isSameDay, isSameMonth, monthGridStart } from '../../lib/datetime'
import { CalendarEventTypeColor, type CalendarEventDto } from '@toga/shared'
import { currentLocaleTag } from '../../lib/formatting'

interface Props {
  cursor: Date
  events: CalendarEventDto[]
  onPickDay: (date: Date) => void
  onPickEvent: (event: CalendarEventDto) => void
}

const MAX_PER_CELL = 3

export default function MonthView({ cursor, events, onPickDay, onPickEvent }: Props) {
  const { t } = useTranslation('agenda')
  const gridStart = monthGridStart(cursor)
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i))
  const today = new Date()
  const monthHeader = days.slice(0, 7)

  return (
    <div className="surface overflow-hidden">
      <div className="grid grid-cols-7 border-b border-base">
        {monthHeader.map((d) => (
          <div
            key={d.toISOString()}
            className="px-2 py-2 text-[10px] uppercase tracking-wider t-faint text-center border-r border-base/30 last:border-r-0"
          >
            {dayLabelShort(d)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 grid-rows-6 h-[600px]">
        {days.map((d) => {
          const dayEvents = events.filter((e) => isEventOnDay(e, d))
          const inMonth = isSameMonth(d, cursor)
          const isToday = isSameDay(d, today)
          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => onPickDay(d)}
              className={[
                'text-left border-r border-b border-base/30 p-1 min-h-0 overflow-hidden flex flex-col',
                isToday ? 'bg-toga-500/8' : '',
                !inMonth ? 'bg-[var(--bg-input-soft)]' : '',
              ].join(' ')}
            >
              <div
                className={[
                  'text-xs font-semibold mb-0.5',
                  isToday ? 'text-toga-700 dark:text-toga-300' : inMonth ? 't-primary' : 't-faint',
                ].join(' ')}
              >
                {d.getDate()}
              </div>
              <div className="flex flex-col gap-0.5 min-h-0 overflow-hidden">
                {dayEvents.slice(0, MAX_PER_CELL).map((e) => {
                  const color = CalendarEventTypeColor[e.type]
                  return (
                    <span
                      key={e.id}
                      onClick={(ev) => {
                        ev.stopPropagation()
                        onPickEvent(e)
                      }}
                      className={[
                        'truncate text-[10px] px-1 py-0.5 rounded border cursor-pointer hover:brightness-125',
                        color.bg,
                        color.text,
                        color.border,
                      ].join(' ')}
                      title={e.title}
                    >
                      {!e.allDay && (
                        <span className="opacity-80 mr-1">
                          {new Date(e.startAt).toLocaleTimeString(currentLocaleTag(), {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      )}
                      {e.title}
                    </span>
                  )
                })}
                {dayEvents.length > MAX_PER_CELL && (
                  <span className="text-[10px] t-faint">
                    {t('monthView.moreEvents', { count: dayEvents.length - MAX_PER_CELL })}
                  </span>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function isEventOnDay(e: CalendarEventDto, d: Date): boolean {
  const start = new Date(e.startAt)
  const rawEnd = e.endAt ? new Date(e.endAt) : new Date(start.getTime() + 60 * 60 * 1000)
  // Fin exclusivo (como WeekView): un evento que termina justo a medianoche no
  // debe pintarse también en el día siguiente. Los de duración cero cuentan 1 ms.
  const end = rawEnd.getTime() > start.getTime() ? rawEnd : new Date(start.getTime() + 1)
  const dayStart = new Date(d)
  dayStart.setHours(0, 0, 0, 0)
  const dayEnd = new Date(d)
  dayEnd.setHours(23, 59, 59, 999)
  return start <= dayEnd && end > dayStart
}
